import { QUEST_GOAL, type Progress } from "../lib/progress";
import { formatInt, formatPace, formatPercent } from "../lib/format";
import type { WallOrder } from "../lib/wall-order";

const PACE_LABEL: Record<Progress["paceStatus"], string> = {
  empty: "NO PACE YET",
  behind: "BEHIND",
  on_pace: "ON PACE",
  ahead: "AHEAD",
  complete: "COMPLETE",
};

const ORDER_OPTIONS: readonly { id: WallOrder; label: string; title: string }[] = [
  {
    id: "featured",
    label: "FEATURED",
    title: "Featured apps first, then ship date and slug. This is the default.",
  },
  {
    id: "new",
    label: "NEW",
    title: "Newest ships first",
  },
  {
    id: "numeric",
    label: "1–N",
    title: "Stable order by ship date, then slug",
  },
  {
    id: "random",
    label: "RANDOM",
    title: "Shuffled live apps. Click again to reshuffle.",
  },
];

type ProgressHUDProps = {
  progress: Progress;
  appsOpen: boolean;
  onToggleApps: () => void;
  order: WallOrder;
  onOrder: (order: WallOrder) => void;
  onReshuffle: () => void;
};

export function ProgressHUD({
  progress,
  appsOpen,
  onToggleApps,
  order,
  onOrder,
  onReshuffle,
}: ProgressHUDProps) {
  const filledWidth = Number.isFinite(progress.percent)
    ? Math.min(100, Math.max(0, progress.percent))
    : 0;
  const target =
    progress.targetPercent !== null && Number.isFinite(progress.targetPercent)
      ? Math.min(100, Math.max(0, progress.targetPercent))
      : null;
  const paceText =
    progress.actualPerDay === null
      ? "no pace yet"
      : `${formatPace(progress.actualPerDay)} per day since the first ship, ${PACE_LABEL[progress.paceStatus].toLowerCase()}`;
  const needText = Number.isFinite(progress.needPerDay)
    ? `${formatPace(progress.needPerDay)} ships per day`
    : "no finite daily rate";
  const label = `Quest progress: ${progress.count} of ${QUEST_GOAL} live apps, ${formatPercent(progress.percent)} filled, ${progress.daysLeft} Pacific days left through 31 December 2026, need ${needText}, pace ${paceText}, streak ${progress.streak}, shipped today ${progress.shippedToday}.`;

  return (
    <div className="hud" role="region" aria-label={label}>
      <div className="viewfinder" aria-hidden="true">
        <span className="vf tl" />
        <span className="vf tr" />
        <span className="vf bl" />
        <span className="vf br" />
      </div>

      <div className="hud-chrome">
        <div className="hud-track">
          <div className="stat stat-count">
            <span className="stat-label">LIVE</span>
            <span className="stat-value">
              {formatInt(progress.count)}
              <span className="stat-slash">/{formatInt(QUEST_GOAL)}</span>
            </span>
          </div>

          <div className="stat stat-filled">
            <span className="stat-label">FILLED</span>
            <span className="stat-value">{formatPercent(progress.percent)}</span>
          </div>

          <div className="stat stat-bar" aria-hidden="true">
            <span className="stat-label">WALL</span>
            <span className="bar">
              <span className="bar-scale">
                <span className="bar-fill" style={{ width: `${filledWidth}%` }} />
                {target === null ? null : (
                  <span
                    className="bar-target"
                    style={{ left: `${target}%` }}
                    title={`Where the wall should be today: ${formatPercent(target)}`}
                  />
                )}
              </span>
            </span>
          </div>

          <div
            className="stat stat-days"
            title="Pacific days from today through 31 Dec 2026, including today."
          >
            <span className="stat-label">
              <span className="label-full">DAYS LEFT</span>
              <span className="label-short">DAYS</span>
            </span>
            <span className="stat-value">{formatInt(progress.daysLeft)}</span>
          </div>

          <div
            className="stat stat-need"
            title="Apps still to ship, divided by Pacific days left including today."
          >
            <span className="stat-label">
              <span className="label-full">NEED / DAY</span>
              <span className="label-short">NEED</span>
            </span>
            <span className="stat-value">{formatPace(progress.needPerDay)}</span>
          </div>

          <div
            className="stat stat-optional"
            title="Average ships per Pacific day since the first ship. Behind means that average is short of the red marker, not of today's ships."
          >
            <span className="stat-label">PACE</span>
            <span className="stat-value">
              {progress.actualPerDay === null ? "—" : formatPace(progress.actualPerDay)}
              <span className="stat-flag">{PACE_LABEL[progress.paceStatus]}</span>
            </span>
          </div>

          <div className="stat stat-optional">
            <span className="stat-label">STREAK</span>
            <span className="stat-value">{formatInt(progress.streak)}</span>
          </div>

          <div className="stat stat-optional">
            <span className="stat-label">SHIPPED TODAY</span>
            <span className="stat-value">{formatInt(progress.shippedToday)}</span>
          </div>
        </div>

        <button
          type="button"
          className="hud-apps"
          onClick={onToggleApps}
          aria-expanded={appsOpen}
          aria-haspopup="dialog"
        >
          {appsOpen ? "CLOSE" : "APPS"}
        </button>
      </div>

      <div className="hud-order" role="group" aria-label="Tile order. Default is featured.">
        <span className="order-kicker">ORDER</span>
        {ORDER_OPTIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            className={order === option.id ? "is-on" : ""}
            aria-pressed={order === option.id}
            title={option.title}
            onClick={() => onOrder(option.id)}
          >
            {option.label}
          </button>
        ))}
        <button type="button" className="order-reshuffle" onClick={onReshuffle}>
          RESHUFFLE
        </button>
      </div>
    </div>
  );
}
